"use client";

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { paymentService } from "@/lib/payment-service";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/utils/supabase/client";
import SubscriptionCard from "@/components/SubscriptionCard"; // FIXED: Changed to default import (no curly braces)
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreditCard, FileText, AlertTriangle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import InvoicesPage from "./Invoices";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { showSuccess, showError } from "@/utils/toast";
import { log_audit_action } from "@/lib/api-queries";
import { Input } from '@/components/ui/input';

const BillingPage = () => {
  const { profile, company, plan, getFeatureAccess, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("subscription");
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');

  // Enhanced role-based access check with logging
  useEffect(() => {
    if (!profile) {
      log_audit_action('system', 'billing_access_attempt', 'billing', { user_id: 'anonymous', action: 'redirect_to_login' });
      navigate('/login');
      return;
    }

    if (!isAdmin && !isSuperAdmin) {
      log_audit_action(profile.id, 'unauthorized_billing_access', 'billing', { attempted_path: window.location.pathname, user_role: profile.role });
      showError("Access to billing is restricted to company admins (primary contact only).");
      navigate('/dashboard');
    }
  }, [isAdmin, isSuperAdmin, profile, navigate]);

  const { data: subscription, refetch: refetchSubscription } = useQuery({
    queryKey: ["subscription", company?.id],
    queryFn: async () => {
      if (!company?.id) return null;
      const { data } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("company_id", company.id)
        .single();
      return data;
    },
  });

  const handleUpgrade = async (planToUpgrade: 'pro' | 'enterprise') => {
    if (!company?.id) {
      showError('No company selected. Please complete onboarding.');
      return;
    }

    try {
      log_audit_action(profile?.id || 'system', 'billing_upgrade_initiated', 'subscription', { plan: planToUpgrade, interval: billingInterval });
      showSuccess(`Redirecting to secure checkout for ${planToUpgrade} plan (£${planToUpgrade === 'pro' ? 79 : 399}/mo equivalent, ${billingInterval}).`);
      await paymentService.createSession(planToUpgrade, company.id, undefined, billingInterval);
    } catch (error) {
      console.error('Upgrade failed:', error);
      showError('Upgrade failed. Please try again or contact support.');
      log_audit_action(profile?.id || 'system', 'billing_upgrade_failed', 'subscription', { plan: planToUpgrade, error: (error as Error).message });
    }
  };

  // Switch interval (updates Supabase + refetches)
  const handleIntervalSwitch = async (interval: 'month' | 'year') => {
    if (!subscription?.id || !company?.id) return;

    try {
      const { error } = await supabase
        .from("subscriptions")
        .update({ billing_interval: interval })
        .eq("id", subscription.id);

      if (error) throw error;

      setBillingInterval(interval);
      refetchSubscription(); // Refresh to show updated interval
      showSuccess(`Billing interval updated to ${interval}!`);
      log_audit_action(profile?.id || 'system', 'billing_interval_changed', 'subscription', { interval });
    } catch (error) {
      showError('Failed to update billing interval.');
      log_audit_action(profile?.id || 'system', 'billing_interval_failed', 'subscription', { interval, error: (error as Error).message });
    }
  };

  const showUpgradePrompt = !getFeatureAccess('pro') && !getFeatureAccess('enterprise');

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6">Billing & Subscriptions (GBP)</h1>
      {showUpgradePrompt && (
        <Alert className="mb-6">
          <AlertDescription>
            Your Basic plan includes up to 5 projects, 2 agents, and 5 clients. Upgrade to Pro (£79/mo) for 20 projects, 50 agents, and 500 clients—or Enterprise (£399/mo) for unlimited scale! 7-day money back guarantee.
          </AlertDescription>
        </Alert>
      )}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="subscription">
            <CreditCard className="mr-2 h-4 w-4" />
            Subscription
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <FileText className="mr-2 h-4 w-4" />
            Invoices
          </TabsTrigger>
        </TabsList>
        <TabsContent value="subscription" className="mt-6">
          <div className="mb-6 flex justify-center items-center space-x-4">
            <span className="text-sm font-medium text-muted-foreground">Billing Cycle:</span>
            <ToggleGroup type="single" value={billingInterval} onValueChange={(value: string) => handleIntervalSwitch(value as 'month' | 'year')}>
              <ToggleGroupItem value="month">Monthly</ToggleGroupItem>
              <ToggleGroupItem value="year">Yearly (Save 10%)</ToggleGroupItem>
            </ToggleGroup>
          </div>
          {subscription ? (
            <SubscriptionCard
              subscription={subscription}
              onUpgrade={(plan) => handleUpgrade(plan)}
              currentPlan={plan}
              billingInterval={billingInterval}
              role={profile?.role}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>No Active Subscription</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">Start with Basic (£5/mo) or upgrade for more features. 7-day money back guarantee.</p>
                <div className="space-y-2">
                  <Button onClick={() => handleUpgrade('pro')} className="w-full">
                    Upgrade to Pro (£79/mo equivalent, {billingInterval})
                  </Button>
                  <Button variant="outline" onClick={() => handleUpgrade('enterprise')} className="w-full">
                    Upgrade to Enterprise (£399/mo equivalent, {billingInterval})
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="invoices" className="mt-6">
          <InvoicesPage />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingPage;